import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { modifier } from 'ember-modifier';
import { PhBook } from 'ember-phosphor-icons';
import Drawer from 'tio-reimagined/components/drawer';
import MarkdownRenderer from 'tio-common/components/markdown-renderer';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';

// Raw import of the markdown file
// @ts-expect-error - raw markdown import
import DATA_SOURCES_MD from 'tio-employee/templates/authenticated/reimagined/DATA_SOURCES.md?raw';

interface DataSourceDocsSignature {
  Args: {
    /**
     * Whether the docs panel is open
     */
    isOpen: boolean;
    /**
     * Callback to close the panel
     */
    onClose: () => void;
    /**
     * Section ID to scroll to (matches markdown header IDs)
     */
    sectionId?: string;
  };
  Element: HTMLDivElement;
}

/**
 * DataSourceDocs Component
 *
 * Renders the DATA_SOURCES.md documentation in a slide-out drawer.
 * Used in development/audit mode to show data source documentation
 * when clicking on DataIndicator badges.
 *
 * Similar to Freestyle - development-only documentation viewer.
 */
export default class DataSourceDocs extends Component<DataSourceDocsSignature> {
  @service declare reimaginedData: ReimaginedDataService;

  @tracked lastScrolledSection: string | undefined;

  get markdownContent() {
    return DATA_SOURCES_MD;
  }

  /**
   * Modifier that scrolls to the target section and highlights it with a fade animation.
   * Searches for headings by text content since marked doesn't generate IDs by default.
   * Uses polling to wait for MarkdownRenderer to complete (async marked import).
   * Only triggers when the section changes to avoid repeated scrolling.
   */
  scrollToSection = modifier((element: HTMLElement, [sectionId]: [string | undefined]) => {
    if (!sectionId || sectionId === this.lastScrolledSection) {
      return;
    }

    // Poll for the heading since MarkdownRenderer loads marked asynchronously
    let attempts = 0;
    const maxAttempts = 20; // Try for up to 2 seconds (20 * 100ms)

    const pollForHeading = () => {
      attempts++;

      // Try finding by ID first (if marked generates IDs)
      let target = element.querySelector(`#${sectionId}`);

      // Fallback: search for heading with matching text
      // Markdown headers like ### section-127 become <h3>section-127</h3>
      if (!target) {
        const headings = element.querySelectorAll('h1, h2, h3, h4, h5, h6');
        for (const heading of headings) {
          const headingText = heading.textContent?.trim().toLowerCase();
          if (headingText === sectionId.toLowerCase()) {
            target = heading;
            break;
          }
        }
      }

      if (target) {
        // Scroll into view smoothly
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });

        // Add highlight animation class (Xcode-style glow effect)
        target.classList.add('docs-highlight-section');

        // Remove after animation completes (3s animation + buffer)
        setTimeout(() => {
          target?.classList.remove('docs-highlight-section');
        }, 3500);

        this.lastScrolledSection = sectionId;
      } else if (attempts < maxAttempts) {
        // Content not rendered yet, try again
        setTimeout(pollForHeading, 100);
      }
    };

    // Start polling after a short initial delay
    setTimeout(pollForHeading, 50);
  });

  <template>
    {{! template-lint-disable no-bare-strings }}
    {{! Dev-only documentation component - translations not needed }}
    <Drawer
      @isOpen={{@isOpen}}
      @onClose={{@onClose}}
      @size="xl"
      @placement="right"
      @closeOnOutsideClick={{true}}
      @closeOnEscapeKey={{true}}
    >
      <:header>
        <div class="flex items-center gap-2">
          <PhBook @size={{24}} class="text-violet-600 dark:text-violet-400" />
          <h2 class="text-gray-900 dark:text-gray-100 font-semibold">
            Data Source Documentation
          </h2>
        </div>
      </:header>

      <:body>
        <div {{this.scrollToSection @sectionId}}>
          {{#if @sectionId}}
            <div
              class="mb-4 px-3 py-2 bg-violet-50 dark:bg-violet-950/50 border border-violet-200 dark:border-violet-800 rounded text-sm"
            >
              <span class="text-violet-600 dark:text-violet-400 font-medium">Section:</span>
              <code
                class="ml-2 px-2 py-0.5 bg-violet-100 dark:bg-violet-900 rounded text-violet-800 dark:text-violet-300"
              >
                #{{@sectionId}}
              </code>
            </div>
          {{/if}}

          <MarkdownRenderer
            @content={{this.markdownContent}}
            class="prose prose-sm max-w-none dark:prose-invert prose-headings:scroll-mt-4 prose-h2:text-xl prose-h2:font-bold prose-h2:text-gray-900 dark:prose-h2:text-gray-100 prose-h2:border-b dark:prose-h2:border-gray-700 prose-h2:pb-2 prose-h3:text-lg prose-h3:font-semibold prose-h3:text-gray-800 dark:prose-h3:text-gray-200 prose-table:text-sm prose-th:bg-gray-100 dark:prose-th:bg-gray-800 prose-th:px-3 prose-th:py-2 prose-td:px-3 prose-td:py-2 prose-td:border-t dark:prose-td:border-gray-700"
          />
        </div>
      </:body>

      <:footer>
        <div class="text-sm text-gray-500 dark:text-gray-400">
          <span>Source: </span>
          <code
            class="text-xs bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded text-gray-700 dark:text-gray-300"
          >
            tio-employee/app/templates/authenticated/reimagined/DATA_SOURCES.md
          </code>
        </div>
      </:footer>
    </Drawer>
  </template>
}
